import React from "react";
import { WbAction, WbSubject } from "../../workbench/types/WbTypes";

export const fallbackSubjectView = {
    title: "N/A",
    broker: "N/A",
    cedent: "ERGO Global Corporate",
    claimant: "ERGO Global Corporate",
    identifier: "SUB-2026-0457",
    uwYear: "N/A",
    mainClass: "Property Damage, Business Interruption",
    currency: "USD",
    period: "01 Jan 2026 - 31 Dec 2026",
    coverage: "Worldwide excluding US & Canada",
    businessType: "Excess of Loss",
    totalLimit: "USD 125,000,000",
    totalPremium: "USD 1,250,000",
};

export const fallbackActions: WbAction[] = [
    {
        actionEventId: "prototype-action-1",
        actionName: "Document uploaded",
        triggeredOn: "2026-05-13T09:15:00Z",
        status: "Completed",
        taskId: "prototype-task-1",
        taskTitle: "ERGO 2026 Property Draft Slip.docx",
    },
    {
        actionEventId: "prototype-action-2",
        actionName: "AI analysis completed",
        triggeredOn: "2026-05-12T16:42:00Z",
        status: "Completed",
        taskId: "prototype-task-2",
        taskTitle: "13 May analysis session",
    },
    {
        actionEventId: "prototype-action-3",
        actionName: "Task updated",
        triggeredOn: "2026-05-12T10:20:00Z",
        status: "Started",
        taskId: "prototype-task-3",
        taskTitle: "Confirm Layer 2 premium",
    },
];

export const formatDate = (value?: string | null) => {
    if (!value) {
        return "";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

export const formatDateTime = (value?: string | null) => {
    if (!value) {
        return "";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    });
};

export const formatActivityDate = (value?: string | null) => {
    if (!value) {
        return "";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    const now = new Date();
    const startOfToday = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
    );
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    if (date >= startOfToday) return "Today";

    if (date >= startOfYesterday && date < startOfToday) return "Yesterday";

    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

export const formatActivityClock = (value?: string | null) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    });
};

export const formatActivityTime = (value?: string | null) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return `${formatActivityDate(value)}, ${formatActivityClock(value)}`;
};

export const formatFileSize = (bytes: number) => {
    if (!bytes) {
        return "-";
    }

    if (bytes < 1024 * 1024) {
        return `${Math.round(bytes / 1024)} KB`;
    }

    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export const getSubjectParty = (subject?: WbSubject | null) =>
    subject?.cedent ??
    subject?.context?.cedent ??
    subject?.context?.claimant ??
    subject?.context?.broker ??
    "";

export const getSubjectTitle = (subject?: WbSubject | null) => {
    if (!subject) {
        return fallbackSubjectView.title;
    }

    const selectedTitle =
        subject.context?.customProperties?.[
            subject.kind === "Claim" ? "claimName" : "title"
        ];
    if (typeof selectedTitle === "string" && selectedTitle.trim()) {
        return selectedTitle;
    }

    const party = getSubjectParty(subject);
    const mainClass = subject.context?.mainClass?.trim();
    const year = subject.context?.uwYear;
    const base = party || subject.identifier;

    if (mainClass && year) {
        return `${base} ${mainClass} ${year}`;
    }

    if (mainClass) {
        return `${base} ${mainClass}`;
    }

    return base || fallbackSubjectView.title;
};

export const getCoveragePeriod = (subject?: WbSubject | null) => {
    const dates = [
        formatDate(subject?.periodFrom),
        formatDate(subject?.periodTo),
    ]
        .filter(Boolean)
        .join(" - ");

    return dates || fallbackSubjectView.period;
};

export const getCompletionPercent = (subject?: WbSubject | null) => {
    const totalTasks = subject?.state.totalTasks ?? 0;
    const openTasks = subject?.state.openTasks ?? 0;

    if (!totalTasks) {
        return 50;
    }

    return Math.round(((totalTasks - openTasks) / totalTasks) * 100);
};

export const noop = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
};

export const copyTextToClipboard = async (text: string) => {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
};
